import { Component, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ClientService } from '../../../core/services/client.service';
import { ContactService } from '../../../core/services/contact.service';
import { Client, Contact } from '../../../core/models/models';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog.component';
import { FormValidators } from '../../../core/validators/form-validators';
import { TaskNotificationService } from '../../../core/services/task-notification.service';

@Component({
  selector: 'app-client-edit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, MatIconModule,ConfirmDialogComponent],
  templateUrl: `client-edit.component.html`,
  styleUrls: [`client-edit.component.css`],
})
export class ClientEditComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private clientService = inject(ClientService);
  private contactService = inject(ContactService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private taskNotificationService = inject(TaskNotificationService);

  isLoading = signal(false);
  loadingClient = signal(true);
  errorMessage = signal<string | null>(null);
  clientId: number | null = null;
  selectedFile = signal<File | null>(null);
  previewDataUrl = signal<string | null>(null);
  currentPhotoUrl = signal<string | null>(null);
  isDragOver = signal(false);

  showDeleteModal = signal(false);
  canDelete = signal(true);
  isArchived = signal(false);
  clientDenomination = signal('');

  // Tab management
  activeTab = signal<'info' | 'contacts'>('info');

  // Contact search management
  contactSearch$ = new Subject<string>();
  contactSearchResults = signal<Contact[]>([]);
  showContactSearch = signal(false);
  selectedContacts = signal<Contact[]>([]);

  clientForm = this.fb.group({
    numeroClient: ['', [Validators.required, FormValidators.numeroClient()]],
    siret: ['', [FormValidators.siret()]],
    denomination: ['', [Validators.required]],
    adresseFacturation: [''],
    ville: [''],
    codePostal: ['', [FormValidators.codePostalFr()]]
  });

  ngOnInit() {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.clientId = +idParam;
      this.loadClient(this.clientId);
    } else {
      this.router.navigate(['/clients']);
    }

    this.setupContactSearch();
  }

  ngOnDestroy() {
    const currentPhotoUrl = this.currentPhotoUrl();
    if (currentPhotoUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(currentPhotoUrl);
    }
  }

  setupContactSearch() {
    this.contactSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => this.contactService.getContacts(term))
    ).subscribe(page => {
      this.contactSearchResults.set(page.content);
    });
  }

  onSearchContact(event: any) {
    const term = event.target.value;
    this.contactSearch$.next(term);
  }

  toggleContactSearch() {
    this.showContactSearch.update(v => !v);
    if (this.showContactSearch()) {
      this.contactSearch$.next('');
    }
  }

  addContact(contact: Contact) {
    if (!this.selectedContacts().some(c => c.id === contact.id)) {
      this.selectedContacts.update(contacts => [...contacts, contact]);
    }
    this.showContactSearch.set(false);
  }

  removeContact(contactId: number | undefined) {
    if (contactId === undefined) return;
    this.selectedContacts.update(contacts => contacts.filter(c => c.id !== contactId));
  }

  setTab(tab: 'info' | 'contacts') {
    this.activeTab.set(tab);
  }

  loadClient(id: number) {
    this.clientService.getClientById(id).subscribe({
      next: (client) => {
        this.clientDenomination.set(client.denomination);
        this.isArchived.set(client.archived || false);
        this.clientForm.patchValue({
          numeroClient: client.numeroClient,
          siret: client.siret,
          denomination: client.denomination,
          adresseFacturation: client.adresseFacturation,
          ville: client.ville,
          codePostal: client.codePostal
        });
        if (client.photo) {
          this.loadCurrentPhoto(id);
        }
        if (client.contacts) {
          this.selectedContacts.set(client.contacts);
        }
        this.checkDeleteStatus(id);
        this.loadingClient.set(false);
      },
      error: (err) => {
        this.errorMessage.set('Erreur lors du chargement du client.');
        this.loadingClient.set(false);
      }
      });
  }

  private loadCurrentPhoto(id: number) {
    this.clientService.getPhotoBlob(id).subscribe({
      next: (blob) => {
        const previousUrl = this.currentPhotoUrl();
        if (previousUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(previousUrl);
        }
        this.currentPhotoUrl.set(URL.createObjectURL(blob));
      },
      error: () => {
        this.currentPhotoUrl.set(null);
      }
    });
  }

  checkDeleteStatus(id: number) {
    this.clientService.canDeleteClient(id).subscribe({
      next: (res) => this.canDelete.set(res),
      error: () => this.canDelete.set(false)
    });
  }

  onDeleteClick() {
    this.showDeleteModal.set(true);
  }

  confirmDelete() {
    if (this.clientId) {
      this.isLoading.set(true);
      this.clientService.deleteClient(this.clientId).subscribe({
        next: () => {
          this.router.navigate(['/clients']);
        },
        error: (err) => {
          this.errorMessage.set('Erreur lors de la suppression/archivage du client.');
          this.isLoading.set(false);
          this.showDeleteModal.set(false);
        }
      });
    }
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
  }

  onFileSelected(event: any) {
    const file = event.target.files?.[0] as File;
    if (file) this.setSelectedFile(file);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) this.setSelectedFile(file);
  }

  removeSelectedFile() {
    this.selectedFile.set(null);
    this.previewDataUrl.set(null);
  }

  private setSelectedFile(file: File) {
    this.selectedFile.set(file);
    const reader = new FileReader();
    reader.onload = () => (this.previewDataUrl.set(reader.result as string));
    reader.readAsDataURL(file);
  }

  onSubmit() {
    if (this.clientForm.valid && this.clientId) {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const clientData = {
        ...this.clientForm.value,
        contacts: this.selectedContacts()
      };

      this.clientService.updateClient(this.clientId, clientData as any).subscribe({
        next: () => {
          if (this.selectedFile() && this.clientId) {
            this.uploadPhoto(this.clientId);
          } else {
            this.router.navigate(['/clients']);
          }
        },
        error: (err) => {
          this.errorMessage.set(err.error?.message || err.error || 'Une erreur est survenue lors de la mise à jour.');
          this.isLoading.set(false);
        }
      });
    }
  }

  uploadPhoto(id: number) {
    if (this.selectedFile()) {
      const taskId = this.taskNotificationService.start('Photo client', 'Upload de l\'image en cours...');
      this.clientService.uploadPhotoAsync(id, this.selectedFile() as File).pipe(
        switchMap(job => this.clientService.waitForPhotoUpload(id, job.jobId))
      ).subscribe({
        next: () => {
          this.taskNotificationService.succeed(taskId, 'Image uploadée.');
          this.router.navigate(['/clients']);
        },
        error: (err) => {
          this.taskNotificationService.fail(taskId, 'L\'upload de l\'image a échoué.');
          this.errorMessage.set('Client mis à jour, mais erreur lors de l\'upload de la photo.');
          this.isLoading.set(false);
        }
      });
    }
  }
}
